import { DocumentNode, gql } from "@apollo/client";
import { useSession } from "hooks/useSession";
import { useToast } from "hooks/useToast";
import { useState } from "react";

export interface GraphQLConnectionProps {
  query: string | DocumentNode;
  variables?: any;
  errorMessage?: string;
  useNewApi?: boolean; // Novo parâmetro para escolher qual API usar
}

export interface GraphQLConnectionErrorProps {
  errorType: "connection" | "auth";
  message?: string;
}

export const useGraphQLAuth = () => {
  const [loading, setLoading] = useState(false);
  const { setToast } = useToast();
  const { signOut } = useSession();

  const connectGraphQLApi = async ({
    query,
    variables,
    errorMessage,
    useNewApi = false,
  }: GraphQLConnectionProps) => {
    setLoading(true);

    try {
      // Convert string query to DocumentNode if needed
      const graphQLQuery = typeof query === "string" ? gql(query) : query;

      // Get appropriate Apollo Client based on useNewApi flag
      const apolloClientModule = await import("../services/apolloClient");
      const apolloClient = useNewApi
        ? apolloClientModule.apolloClientNew
        : apolloClientModule.apolloClient;

      // Check if it's a mutation or query by examining the operation
      const queryString =
        typeof query === "string" ? query : query.loc?.source.body || "";
      const isMutation = queryString
        .trim()
        .toLowerCase()
        .startsWith("mutation");

      let result;
      if (isMutation) {
        // Use mutate for mutations
        result = await apolloClient.mutate({
          mutation: graphQLQuery,
          variables,
          fetchPolicy: "network-only",
        });
      } else {
        // Use query for queries
        result = await apolloClient.query({
          query: graphQLQuery,
          variables,
          fetchPolicy: "network-only",
        });
      }

      setLoading(false);
      return result;
    } catch (error: any) {
      setLoading(false);

      // Handle authentication errors
      if (error.networkError?.statusCode === 401) {
        sessionStorage.setItem("unauthorized", "true");
        window.location.replace("/");
        signOut();

        const authError: GraphQLConnectionErrorProps = {
          errorType: "auth",
          message: "Unauthorized",
        };
        throw authError;
      }

      const message = error.message || errorMessage;

      // Handle other errors
      setToast({
        message: message || "Erro ao conectar com o servidor GraphQL",
        type: "error",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      const connectionError: GraphQLConnectionErrorProps = {
        errorType: "connection",
        message: error.message,
      };
      throw connectionError;
    }
  };

  const cancelRequest = () => {
    // Apollo Client handles request cancellation internally
    setLoading(false);
  };

  return {
    connectGraphQLApi,
    cancelRequest,
    loading,
  };
};
